import React from 'react';
import { IconButton, Tooltip } from '@material-ui/core';
import SearchInput from '../SearchInput';
import DateRangePicker from '../DateRangePicker';
import Button from '../Button';
import { useStyles } from './styles';

/**
 * Універсальний компонент PageHeader
 *
 * @param {Object} props
 * @param {string} props.title - Заголовок сторінки (обов'язково)
 * @param {string} props.subtitle - Підзаголовок сторінки (опціонально)
 * @param {Object} props.searchInput - Конфігурація пошуку (опціонально)
 * @param {string} props.searchInput.value - Значення пошуку
 * @param {function} props.searchInput.onChange - Обробник зміни пошуку
 * @param {string} props.searchInput.placeholder - Placeholder для пошуку
 * @param {Object} props.dateRangePicker - Конфігурація дата пікера (опціонально)
 * @param {Date} props.dateRangePicker.initialStartDate - Початкова дата
 * @param {Date} props.dateRangePicker.initialEndDate - Кінцева дата
 * @param {function} props.dateRangePicker.onChange - Обробник зміни дат
 * @param {Object} props.button - Конфігурація кнопки (опціонально)
 * @param {function} props.button.onClick - Обробник кліку
 * @param {string} props.button.children - Текст кнопки
 * @param {string} props.button.variant - Варіант кнопки (primary/secondary/outlined)
 * @param {Object} props.iconButton - Конфігурація icon-кнопки в правому нижньому куті (опціонально)
 * @param {function} props.iconButton.onClick - Обробник кліку
 * @param {React.ReactNode} props.iconButton.icon - Іконка
 * @param {string} props.iconButton.ariaLabel - Aria-label для доступності
 * @param {boolean} props.iconButton.disabled - Чи кнопка вимкнена
 * @param {string} props.iconButton.tooltip - Текст підказки при наведенні (опціонально)
 * @param {React.ReactNode} props.controls - Додаткові елементи керування (опціонально)
 */
const PageHeader = ({
    title,
    subtitle,
    searchInput = null,
    dateRangePicker = null,
    button = null,
    iconButton = null,
    controls = null,
}) => {
    const classes = useStyles();

    return (
        <div className={classes.headerWrapper}>
            <div className={classes.pageHeader}>
                <div className={classes.headerRow}>
                    <div className={classes.headerLeft}>
                        <h1 className={classes.pageTitle}>{title}</h1>
                        {subtitle && <p className={classes.pageSubtitle}>{subtitle}</p>}
                    </div>

                    {searchInput && (
                        <div className={classes.searchInputWrapper}>
                            <SearchInput
                                value={searchInput.value}
                                onChange={searchInput.onChange}
                                onSearch={searchInput.onSearch}
                                placeholder={searchInput.placeholder || "Пошук"}
                            />
                        </div>
                    )}
                </div>

                {(dateRangePicker || controls || button || iconButton) && (
                    <div className={classes.controlsRow}>
                        {dateRangePicker && (
                            <div className={classes.dateRangePickerWrapper}>
                                <DateRangePicker
                                    initialStartDate={dateRangePicker.initialStartDate}
                                    initialEndDate={dateRangePicker.initialEndDate}
                                    onChange={dateRangePicker.onChange}
                                    disableFuture={dateRangePicker.disableFuture}
                                    placeholder={dateRangePicker.placeholder}
                                />
                            </div>
                        )}

                        {controls}

                        {button && (
                            <div className={classes.buttonWrapper}>
                                <Button
                                    variant={button.variant || 'primary'}
                                    onClick={button.onClick}
                                    disabled={button.disabled}
                                    loading={button.loading}
                                    size={button.size || 'default'}
                                >
                                    {button.children}
                                </Button>
                            </div>
                        )}

                        {iconButton && (
                            <div className={classes.iconButtonWrapper}>
                                {iconButton.tooltip ? (
                                    <Tooltip title={iconButton.tooltip} arrow>
                                        <IconButton
                                            className={classes.iconButton}
                                            onClick={iconButton.onClick}
                                            disabled={iconButton.disabled}
                                            aria-label={iconButton.ariaLabel}
                                            size="small"
                                        >
                                            {iconButton.icon}
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <IconButton
                                        className={classes.iconButton}
                                        onClick={iconButton.onClick}
                                        disabled={iconButton.disabled}
                                        aria-label={iconButton.ariaLabel}
                                        size="small"
                                    >
                                        {iconButton.icon}
                                    </IconButton>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PageHeader;
